import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { StatusBadge } from "../components/Badges";
import { Factory, Truck, ShoppingCart, ClipboardList, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  production: { label: "Produktion", color: "#EB75B5", icon: Factory },
  delivery:   { label: "Lieferung",  color: "#3b82f6", icon: Truck },
  purchase:   { label: "Einkauf",    color: "#d97706", icon: ShoppingCart },
  order:      { label: "Bestellung", color: "#10b981", icon: ClipboardList },
  cleaning:   { label: "Reinigung",  color: "#B45309", icon: Sparkles },
};

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function buildMonth(anchor) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const firstWeekday = (first.getDay() + 6) % 7; // Mo=0
  const start = new Date(first);
  start.setDate(1 - firstWeekday);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [anchor, setAnchor] = useState(new Date());
  const [selected, setSelected] = useState(new Date());
  const [dragging, setDragging] = useState(null); // {type, id, ref}
  const [dropTarget, setDropTarget] = useState(null);
  const [activeTypes, setActiveTypes] = useState(() => {
    try {
      const saved = localStorage.getItem("popcrn:calendar-types");
      if (saved) return JSON.parse(saved);
    } catch (_e) { /* noop */ }
    return { production: true, delivery: true, purchase: true, order: true, cleaning: true };
  });

  useEffect(() => {
    try { localStorage.setItem("popcrn:calendar-types", JSON.stringify(activeTypes)); } catch (_e) { /* noop */ }
  }, [activeTypes]);

  const load = () => api.get("/calendar").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  useEffect(() => { load(); }, []);

  const visibleEvents = useMemo(
    () => events.filter((e) => activeTypes[e.type]),
    [events, activeTypes]
  );

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of visibleEvents) {
      if (!e.date) continue;
      (map[e.date] = map[e.date] || []).push(e);
    }
    return map;
  }, [visibleEvents]);

  const days = useMemo(() => buildMonth(anchor), [anchor]);
  const monthLabel = anchor.toLocaleDateString("de-DE", { month: "long", year: "numeric" });

  const move = (delta) => {
    const d = new Date(anchor); d.setMonth(d.getMonth() + delta); setAnchor(d);
  };

  const onDragStart = (e, ev) => {
    setDragging({ type: ev.type, id: ev.id, ref: ev.ref });
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", ev.ref || ev.id); } catch (_e) { /* noop */ }
  };
  const onDragEnd = () => { setDragging(null); setDropTarget(null); };
  const onDragOverDay = (e, key) => {
    if (!dragging) return;
    e.preventDefault(); e.dataTransfer.dropEffect = "move";
    if (dropTarget !== key) setDropTarget(key);
  };
  const onDropDay = async (e, key) => {
    e.preventDefault();
    if (!dragging) return;
    const payload = { ...dragging, new_date: key };
    setDragging(null); setDropTarget(null);
    try {
      const { data } = await api.post("/calendar/reschedule", { type: payload.type, id: payload.id, new_date: key });
      const finalDate = data?.new_date || key;
      setEvents((evts) => evts.map((ev) => (ev.id === payload.id && ev.type === payload.type ? { ...ev, date: finalDate } : ev)));
      if (data?.notice) toast.warning(data.notice);
      else toast.success(`${payload.ref} verschoben auf ${finalDate}`);
      if (payload.type === "production") load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reschedule fehlgeschlagen");
      load();
    }
  };

  const selKey = ymd(selected);
  const selectedEvents = eventsByDay[selKey] || [];
  const todayKey = ymd(new Date());

  return (
    <div data-testid="page-calendar">
      <PageHeader
        eyebrow="Planung"
        title="Kalender"
        description="Alle Termine im Überblick. Zieh einen Termin auf einen anderen Tag, um ihn umzuplanen."
      />

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="bg-white border border-[var(--border)] rounded-2xl p-4" data-testid="calendar-widget">
          {/* Month header */}
          <div className="flex items-center justify-between mb-3 px-2">
            <div className="font-display font-black text-2xl capitalize">{monthLabel}</div>
            <div className="flex items-center gap-1">
              <button data-testid="btn-prev-month" onClick={() => move(-1)}
                      className="h-9 w-9 rounded-full border border-[var(--border)] flex items-center justify-center hover:bg-neutral-50"><ChevronLeft size={16} /></button>
              <button data-testid="btn-today" onClick={() => { setAnchor(new Date()); setSelected(new Date()); }}
                      className="h-9 px-4 rounded-full border border-[var(--border)] text-xs font-semibold hover:bg-neutral-50">Heute</button>
              <button data-testid="btn-next-month" onClick={() => move(1)}
                      className="h-9 w-9 rounded-full border border-[var(--border)] flex items-center justify-center hover:bg-neutral-50"><ChevronRight size={16} /></button>
            </div>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map((w) => (
              <div key={w} className="overline text-center py-2">{w}</div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const key = ymd(d);
              const inMonth = d.getMonth() === anchor.getMonth();
              const isToday = key === todayKey;
              const isSelected = key === selKey;
              const dayEvents = eventsByDay[key] || [];
              const isDrop = dropTarget === key && !!dragging;
              return (
                <button
                  key={key}
                  data-testid={`day-${key}`}
                  onClick={() => setSelected(d)}
                  onDragOver={(e) => onDragOverDay(e, key)}
                  onDragLeave={() => setDropTarget((cur) => (cur === key ? null : cur))}
                  onDrop={(e) => onDropDay(e, key)}
                  className={`text-left min-h-[92px] rounded-xl p-1.5 border transition-colors
                    ${inMonth ? "bg-white" : "bg-neutral-50 text-[var(--text-muted)]"}
                    ${isSelected ? "border-[var(--pop-pink)] ring-2 ring-[var(--pop-pink)]/30" : "border-[var(--border)]"}
                    ${isDrop ? "!border-[var(--pop-pink)] bg-pink-50" : ""}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-mono ${isToday ? "bg-[var(--pop-pink)] text-white rounded-full px-1.5 py-0.5" : ""}`}>
                      {d.getDate()}
                    </span>
                    {dayEvents.length > 0 && (
                      <span className="overline text-[10px]">{dayEvents.length}</span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map((ev) => {
                      const meta = TYPE_META[ev.type] || TYPE_META.order;
                      return (
                        <div
                          key={`${ev.type}-${ev.id}`}
                          draggable
                          onDragStart={(e) => onDragStart(e, ev)}
                          onDragEnd={onDragEnd}
                          data-testid={`event-chip-${ev.ref}`}
                          className="truncate rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-white cursor-grab active:cursor-grabbing"
                          style={{ background: ev.color || meta.color }}
                          title={ev.title}
                        >
                          {ev.ref || ev.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-[var(--text-muted)] font-mono">+{dayEvents.length - 3}</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-4 border-t border-[var(--border)] pt-3 flex flex-wrap gap-2 text-xs">
            {Object.entries(TYPE_META).map(([k, v]) => {
              const on = activeTypes[k];
              return (
                <button
                  key={k}
                  data-testid={`filter-${k}`}
                  onClick={() => setActiveTypes((t) => ({ ...t, [k]: !t[k] }))}
                  className={`pop-pill transition-opacity ${on ? "opacity-100" : "opacity-30 grayscale"}`}
                  style={{ background: v.color, color: "#fff" }}
                  title={on ? `${v.label} ausblenden` : `${v.label} einblenden`}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected day panel */}
        <div className="bg-white border border-[var(--border)] rounded-2xl p-6" data-testid="calendar-day">
          <div className="overline mb-1">Tag</div>
          <h2 className="font-display font-black text-2xl mb-4">
            {new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "2-digit", month: "long" }).format(selected)}
          </h2>

          {selectedEvents.length === 0 && (
            <div className="text-sm text-[var(--text-muted)] py-6">Keine Termine an diesem Tag.</div>
          )}

          <ul className="space-y-3">
            {selectedEvents.map((e) => {
              const meta = TYPE_META[e.type] || TYPE_META.order;
              const Icon = meta.icon;
              return (
                <li key={`${e.type}-${e.id}`} data-testid={`event-${e.ref}`}
                    className="flex items-center gap-3 p-3 rounded-xl border border-[var(--border)]">
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center"
                       style={{ background: e.color || meta.color, color: "#fff" }}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{e.title}</div>
                    <div className="overline">{meta.label}</div>
                  </div>
                  <StatusBadge status={e.status} />
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
